import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Upload, Users, Volume2, PenLine } from 'lucide-react';
import Layout from '../components/Layout';
import { Badge, Card } from '../components/ui';
import { linkButtonClass } from '../utils/linkButtonClass';

/**
 * 機能ガイドページ（/guide）。
 *
 * 「何ができるか」の一覧はトップページが担うので、本ページは
 * 「実際にどう動くか」を手順で見せることに絞る。
 *
 * 動画の埋め込みは撮影までの間いったん外してある（「準備中」の空枠が4つ並ぶ状態を避けるため）。
 * 戻すときは components/YouTubeEmbed と config/guideVideos.ts が残してあるので、
 * GuideSection に videoKey を復帰させ、lead の下に <YouTubeEmbed> を置き直せばよい。
 *
 * 対応プランの表記は backend の機能ゲートに対応する:
 * diarization=standard,unlimited / manual_edit=unlimited / audio_retention=unlimited。
 */

interface GuideSection {
  id: string;
  icon: JSX.Element;
  title: string;
  lead: string;
  /** 対応プラン。全プランで使える機能は undefined */
  plans?: string;
  steps: { heading: string; body: string }[];
  tip?: string;
}

const sections: GuideSection[] = [
  {
    id: 'flow',
    icon: <Upload className="w-6 h-6 text-white" aria-hidden="true" />,
    title: 'アップロードから書き起こしまで',
    lead: 'セッションの録音ファイルを選んでアップロードするだけ。あとは待つだけで、タイムスタンプ付きのテキストができあがります。',
    steps: [
      {
        heading: '1. 録音ファイルを選ぶ',
        body: '音声（MP3 / WAV / M4A / FLAC）と動画（MP4 / MOV / M4V / WEBM）に対応しています。動画の場合は音声を自動で抽出します。ファイルサイズの上限は2GB（約6時間の音声に相当）です。',
      },
      {
        heading: '2. TRPGシステムを選ぶ',
        body: 'クトゥルフ神話TRPG、D&D、ソード・ワールド、インセイン、シノビガミなど、遊んだシステムを選ぶと専用辞書が適用され、固有名詞やダイス用語の認識精度が上がります。',
      },
      {
        heading: '3. 待つ',
        body: '3時間の録音でおよそ15分。処理中はページを閉じても問題ありません。完了するとダッシュボードから結果を開けます。',
      },
      {
        heading: '4. 結果を確認・ダウンロード',
        body: '発話ごとにタイムスタンプが付いた状態で表示されます。TXT / JSON / HTML の形式でダウンロードできます。',
      },
    ],
    tip: '書き起こし結果は完了から8時間で自動削除されます（無制限プランは7日間）。必要なものは早めにダウンロードしてください。',
  },
  {
    id: 'diarization',
    icon: <Users className="w-6 h-6 text-white" aria-hidden="true" />,
    title: '話者分離：誰が話したかを分ける',
    lead: '複数人で録った1本の音声から、話している人を自動で聞き分けて、発言ごとに「話者1」「話者2」…と振り分けます。GMとPLの発言が混ざらず、ログとして読める形になります。',
    plans: 'スタンダードプラン / 無制限プラン',
    steps: [
      {
        heading: '自動で話者を割り当てる',
        body: '書き起こしと同時に話者分離が走ります。事前に人数を入力したり、話者ごとに録音を分けたりする必要はありません。1本のミックス音声のままで大丈夫です。',
      },
      {
        heading: '話者名を実際の名前に変える',
        body: '結果画面で「話者1」のラベルをクリックし、GMやキャラクター名に付け替えられます。変更は同じ話者のすべての発言に一括で反映されます。',
      },
      {
        heading: 'ダウンロードにも反映される',
        body: '付け替えた話者名は、TXT / JSON / HTML のダウンロード結果にもそのまま反映されます。付け替えてからダウンロードすれば、そのまま公開用のログとして使えます。',
      },
    ],
    tip: '各自がヘッドセットで話しているなど、声がはっきり分かれている録音ほど精度が上がります。同じ部屋の1本のマイクで録った音声でも分離できますが、声が重なった部分は苦手です。',
  },
  {
    id: 'playback',
    icon: <Volume2 className="w-6 h-6 text-white" aria-hidden="true" />,
    title: 'テキスト再生：本文から音声を聞き直す',
    lead: '「ここ、本当にこう言ってたっけ？」を、その場で確認できます。書き起こしの各発言から、対応する場面の音声をそのまま再生できる機能です。',
    plans: '無制限プランのみ',
    steps: [
      {
        heading: '発言ごとに再生する',
        body: '結果画面の各発言に再生ボタンが付きます。押すと、その発言の区間だけを音声で再生します。長い録音の中から目的の場面を探す必要はありません。',
      },
      {
        heading: '聞きながら直せる',
        body: '再生しながらテキスト編集を行えるので、聞き取りづらかった固有名詞やダイス目をその場で確認して修正できます。',
      },
      {
        heading: '音声は7日間だけ保持',
        body: '聞き直しのために、処理済みの音声を結果と同じ7日間だけサーバー上に保持します。アクセスできるのはご本人だけで、期限を過ぎると結果とともに自動削除されます。',
      },
    ],
    tip: '他のプランでは処理が終わった時点で音声を削除するため、テキスト再生は無制限プラン限定です。聞き直しながら仕上げたい方はこのプランをお選びください。',
  },
  {
    id: 'editing',
    icon: <PenLine className="w-6 h-6 text-white" aria-hidden="true" />,
    title: 'テキスト編集：気になるところを直す',
    lead: '音声認識はどうしても完璧にはなりません。固有名詞の誤変換や、話者の取り違えを、結果画面でそのまま直せます。',
    plans: '無制限プランのみ',
    steps: [
      {
        heading: '本文を直す',
        body: '発言のテキストをクリックすると編集できます。キャラクター名やシナリオ固有の用語など、誤変換が気になる箇所をその場で書き換えられます。',
      },
      {
        heading: '話者を付け替える',
        body: '話者の割り当てが入れ替わっている発言は、正しい話者に付け替えられます。本文の修正と話者の修正はそれぞれ独立して保存されるので、片方だけ直しても問題ありません。',
      },
      {
        heading: '修正はダウンロードにも反映',
        body: '編集した内容は保存され、TXT / JSON / HTML のダウンロード結果にも反映されます。整えたログをそのまま配布用に使えます。',
      },
    ],
    tip: '話者名の一括変更（「話者1」→ キャラクター名）はスタンダードプランでも使えます。発言ごとの本文修正・話者の付け替えが無制限プラン限定の機能です。',
  },
];

export default function GuidePage() {
  return (
    <Layout showPromo>
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-hover transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            トップに戻る
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-text-heading mb-3">機能ガイド</h1>
          <p className="text-text-sub leading-relaxed">
            otomochi の主な機能を、手順を追って紹介します。
          </p>
        </div>

        {/* 目次 */}
        <nav aria-label="目次" className="mb-14">
          <ul className="flex flex-wrap justify-center gap-3">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="inline-flex items-center rounded-control bg-card px-4 py-2 text-sm font-medium text-text-sub shadow-card transition-colors hover:text-primary"
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-16">
          {sections.map((section) => {
            return (
              // scroll-mt: 目次アンカーで飛んだとき見出しが画面上端に貼り付かないよう余白を確保
              <section key={section.id} id={section.id} aria-labelledby={`${section.id}-heading`} className="scroll-mt-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center flex-shrink-0">
                    {section.icon}
                  </div>
                  <div>
                    <h2
                      id={`${section.id}-heading`}
                      className="text-2xl font-bold text-text-heading leading-snug"
                    >
                      {section.title}
                    </h2>
                    {section.plans && (
                      <Badge variant="info" size="sm" className="mt-2">
                        対応プラン: {section.plans}
                      </Badge>
                    )}
                  </div>
                </div>

                <p className="text-text-sub leading-relaxed mb-6">{section.lead}</p>

                <div className="space-y-3">
                  {section.steps.map((step) => (
                    <Card key={step.heading}>
                      <h3 className="text-base font-bold text-text-heading mb-1.5">
                        {step.heading}
                      </h3>
                      <p className="text-sm text-text-sub leading-relaxed">{step.body}</p>
                    </Card>
                  ))}
                </div>

                {section.tip && (
                  <div className="mt-4 rounded-panel border border-accent/30 bg-accent/5 px-5 py-4">
                    <p className="text-sm text-text-sub leading-relaxed">{section.tip}</p>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* CTA */}
        <Card className="mt-16 text-center py-12 px-6">
          <h2 className="text-2xl font-bold text-text-heading mb-3">使ってみませんか</h2>
          <p className="text-text-sub mb-8 leading-relaxed">
            無料プランなら、月3回・合計15分まで書き起こしをお試しいただけます。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/login" className={linkButtonClass('primary')}>
              <span>無料で始める</span>
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </Link>
            <Link to="/pricing" className={linkButtonClass('secondary')}>
              料金プランを見る
            </Link>
          </div>
          <p className="text-sm text-text-sub mt-8">
            使い方でわからないことは
            <Link
              to="/faq"
              className="mx-1 text-accent hover:text-accent-hover font-medium underline underline-offset-2"
            >
              よくある質問
            </Link>
            もご覧ください。
          </p>
        </Card>
      </main>
    </Layout>
  );
}
